import styles from './simulator-v2-help.module.scss';

export interface SimulatorV2HelpSection {
  id: string;
  title: string;
  /** selectedAction / active tab label values that should auto-expand this section */
  matchKeys: string[];
  body: JSX.Element;
}

/**
 * Accessible emoji wrapper for tip/required callouts.
 * @param props - symbol and accessible label
 * @example <Emoji symbol="💡" label="tip" />
 */
function Emoji({ symbol, label }: { symbol: string; label: string }) {
  return (
    <span role="img" aria-label={label}>
      {symbol}
    </span>
  );
}

/**
 * Renders the 💡 tip emoji with an accessible label.
 * @example <Tip />
 */
const Tip = () => <Emoji symbol="💡" label="tip" />;

/**
 * Renders the ⚠️ required emoji with an accessible label.
 * @example <Required />
 */
const Required = () => <Emoji symbol="⚠️" label="required" />;

/**
 * Task-first Simulator V2 user guide, rendered inside SimulatorV2HelpPanel.
 * Content here is the source of truth for the in-app help panel.
 */
export const simulatorV2HelpSections: SimulatorV2HelpSection[] = [
  {
    id: 'quick-start',
    title: 'Quick Start Cheat Sheet',
    matchKeys: [],
    body: (
      <div className={styles.sectionBody}>
        <p className={styles.calloutTip}>
          <Tip /> New here? Run through one of these once and it'll feel effortless live.
        </p>
        <p className={styles.taskLabel}>Play an existing Journey</p>
        <ol>
          <li>Search for the scenario by name.</li>
          <li>Click the green ▶ on that row.</li>
          <li>Switch to the Agent app tab — the contact arrives automatically.</li>
          <li>Handle it, then end it when you're done.</li>
        </ol>
        <p className={styles.taskLabel}>Personalize a Journey for a prospect</p>
        <ol>
          <li>Click "…" on a Journey you like, then choose Clone.</li>
          <li>Give the copy a new name, e.g. "Demo for [Prospect Name]".</li>
          <li>Open the copy, choose Edit, and update the customer's name, email, or notes.</li>
          <li>Save, then click ▶ to run it.</li>
        </ol>
        <p className={styles.taskLabel}>Build a brand-new scenario</p>
        <ol>
          <li>Click Create New Journey.</li>
          <li>Choose the contact type and channel on Journey Details.</li>
          <li>Fill in first name, last name, and email on Customer Details.</li>
          <li>Optionally add notes, history, or CRM records for realism.</li>
          <li>Turn on the Copilot features you want to demonstrate.</li>
          <li>Save, then click ▶ to run it.</li>
        </ol>
      </div>
    ),
  },
  {
    id: 'what-is-simulator',
    title: 'What Is Simulator?',
    matchKeys: [],
    body: (
      <div className={styles.sectionBody}>
        <p>
          A demo control room built into CXone Agent. Build, save, and replay realistic
          customer contacts — phone call, chat, email, social message, video call, or
          voicemail — with a single click.
        </p>
        <p>
          Each saved scenario is a <strong>Journey</strong>. Starting a Journey makes a
          simulated customer appear in the live Agent screen, exactly as if a real
          customer had just gotten in touch.
        </p>
        <ul>
          <li>No real phone line, chat widget, or social account needed.</li>
          <li>Every demo is repeatable — the prospect sees the same polished story every time.</li>
          <li>Every demo is customizable — change the channel or conversation in minutes.</li>
          <li>Journeys are saved to your account and can be shared with the team.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'find-and-play',
    title: 'How Do I Find and Play an Existing Journey?',
    matchKeys: ['All Journeys', 'Crafted By Me', 'Favorites'],
    body: (
      <div className={styles.sectionBody}>
        <ul>
          <li><strong>All Journeys</strong> — every saved Journey across the whole team.</li>
          <li><strong>Crafted By Me</strong> — only the Journeys you personally built.</li>
          <li><strong>Favorites</strong> — Journeys you've starred for quick access.</li>
        </ul>
        <p className={styles.taskLabel}>To start a demo</p>
        <ol>
          <li>Type into the search box to filter by name, or use the filter icon to narrow by creator.</li>
          <li>Click the green ▶ Start Simulation button on that row.</li>
          <li>Switch to the Agent app tab — within a second or two it rings, or shows an incoming chat/email.</li>
          <li>Handle the contact as you would in real life, then end it when you're done.</li>
        </ol>
        <p className={styles.calloutTip}>
          <Tip /> This is a two-click demo — one click to pick the Journey, one to start it. Practice it once or twice so it feels effortless live.
        </p>
        <p className={styles.taskLabel}>The "…" menu on any row</p>
        <ul>
          <li><strong>Start Simulation</strong> — anyone can send it to the live Agent screen.</li>
          <li><strong>Edit</strong> — creator only.</li>
          <li><strong>Add / Remove Favorite</strong> — anyone can star it.</li>
          <li><strong>Clone</strong> — anyone can make their own copy under a new name.</li>
          <li><strong>Delete</strong> — creator only, after confirming.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'archived-journeys',
    title: 'How Do I Archive or Restore a Journey?',
    matchKeys: ['Archived'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          The <strong>Archived</strong> view lists Journeys you've put aside without
          deleting them.
        </p>
        <ul>
          <li>Archive a Journey from its "…" menu to move it out of your active lists.</li>
          <li>Open the Archived view to find it again and restore it back to active.</li>
          <li>Archiving doesn't delete anything — it's reversible, unlike Delete.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'build-from-scratch',
    title: 'How Do I Build a Brand-New Journey?',
    matchKeys: ['Create New Journey', 'Edit Journey'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Click <strong>Create New Journey</strong> in the top-right corner. The work
          area turns into a form with several tabs. Fill in what you need, then click
          Save — Save and Cancel are always visible at the top.
        </p>
        <p className={styles.calloutTip}>
          <Tip /> Open the Help icon next to the tab bar while building — it jumps straight
          to that tab's section below.
        </p>
        <ul>
          <li>Journey Details — name the scenario, pick the media/channel.</li>
          <li>Customer Details — who the pretend customer is.</li>
          <li>Overview — notes on the customer's profile.</li>
          <li>Interaction History — past conversations with your company.</li>
          <li>Contact History — past outreach attempts to the customer.</li>
          <li>External Data — simulated CRM records.</li>
          <li>Copilot — turn the AI assistant on or off (voice, video, chat, email only).</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'journey-details',
    title: 'Journey Details — Name the Scenario',
    matchKeys: ['Journey Details'],
    body: (
      <div className={styles.sectionBody}>
        <ul>
          <li><span className={styles.required}><Required /> Required —</span> <strong>Journey Name</strong>: a unique, easy-to-recognize name. Names can't repeat.</li>
          <li><strong>Journey Description</strong>: a short note for your own reference.</li>
          <li><span className={styles.required}><Required /> Required —</span> <strong>Media</strong>: Voice, Digital, Voicemail, or Video Call.</li>
          <li><strong>Channel</strong> (Digital only): Email, Chat, X, Instagram, Facebook, or LinkedIn.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'customer-details',
    title: 'Customer Details — Who Is the Customer?',
    matchKeys: ['Customer Details'],
    body: (
      <div className={styles.sectionBody}>
        <p className={styles.calloutTip}>
          <Required /> First name, last name, and a valid email are always required; everything
          else is optional.
        </p>
        <ul>
          <li><strong>Basic identity</strong> — name, email, customer ID.</li>
          <li><strong>Address</strong> — street, city, state, country, ZIP.</li>
          <li><strong>Routing</strong> — an optional screen-pop page for the agent, and how it opens.</li>
          <li><strong>Voice-call details</strong> — ringing/hold/conference state, verification status, AI confidence score.</li>
          <li><strong>Email details</strong> — whether replies need manager approval.</li>
          <li><strong>Auto Summary</strong> — off, on, or your own custom summary text (voice/chat).</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'overview-notes',
    title: 'Overview — Notes on the Customer\'s Profile',
    matchKeys: ['Overview'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Add short notes that appear on the customer's profile once the demo starts —
          e.g. a note left by a previous agent. Add as many as you like.
        </p>
        <p className={styles.calloutTip}>
          <Required /> Each note needs a title, a subtitle, and the name of the agent who wrote it.
        </p>
      </div>
    ),
  },
  {
    id: 'interaction-history',
    title: 'Interaction History — Past Conversations',
    matchKeys: ['Interaction History'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          List earlier conversations this pretend customer already had with your
          company: channel, inbound/outbound, status, date, and which agent handled it.
        </p>
        <p className={styles.calloutTip}>
          <Tip /> Great for showing the AI recalling a customer's past history during a demo.
        </p>
      </div>
    ),
  },
  {
    id: 'contact-history',
    title: 'Contact History — Past Outreach Attempts',
    matchKeys: ['Contact History'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Works the same way as Interaction History, but for attempts your company made
          to reach the customer — e.g. a follow-up call that went unanswered.
        </p>
      </div>
    ),
  },
  {
    id: 'external-data',
    title: 'External Data — Simulated CRM Records',
    matchKeys: ['External Data'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Simulates case or order information you might normally pull from a separate
          CRM, so agents can show a "full picture" of the customer without connecting
          to a real one.
        </p>
        <ul>
          <li>Add a current record plus any related records.</li>
          <li>Optional switches show a loyalty card section or a support-ticket section.</li>
        </ul>
      </div>
    ),
  },
  {
    id: 'call-transcript',
    title: 'Call Transcript — What the Agent Reads on a Call',
    matchKeys: ['Call Transcript'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Only appears for Voice Journeys. It is the conversation between the agent and
          the customer shown in the middle of the screen when the agent clicks
          <strong> Show Transcript</strong> on the call.
        </p>
        <ul>
          <li>Edit any line, switch it between agent and customer, reorder or remove it.</li>
          <li>Add new agent, customer or system lines at the bottom.</li>
          <li>
            System lines are call events such as "Call on hold". Pick their icon (hold, resumed,
            error or success) from <strong>Event icon</strong>, or choose{' '}
            <strong>Upload custom image</strong> to use your own small image. They can be edited or
            removed, but stay as events.
          </li>
          <li>
            <strong>Transcript playback</strong>: <strong>Auto run</strong> shows the messages one by
            one automatically; <strong>Manual control</strong> shows the next message each time the
            agent presses the <strong>Up arrow</strong> key.
          </li>
        </ul>
        <p className={styles.calloutTip}>
          <Tip /> Until you change anything, a standard sample conversation is used.
          <strong> Reset to default transcript</strong> brings it back.
        </p>
      </div>
    ),
  },
  {
    id: 'copilot',
    title: 'Copilot — Turning the AI On or Off',
    matchKeys: ['Copilot'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Only appears for Voice, Video Call, Digital Chat, and Digital Email Journeys —
          it controls the AI assistant shown to the agent.
        </p>
        <ul>
          <li><strong>Activate Copilot</strong> — turns on the whole AI panel.</li>
          <li><strong>Journey History</strong> — AI-written summaries of past interactions.</li>
          <li><strong>Agent Transfer Summary</strong> — auto handoff note on transfer.</li>
          <li><strong>Bot Transcript</strong> (chat only) — the conversation with a chatbot before reaching the agent.</li>
        </ul>
        <p className={styles.calloutTip}>
          <Tip /> For Voice and Chat, you can upload your own script controlling exactly what
          the customer/agent say and what the AI suggests. A template is available to
          download and re-upload; removing your file falls back to the standard version.
        </p>
      </div>
    ),
  },
  {
    id: 'process-automation',
    title: 'Process Automation — Tasks the Agent Can Run',
    matchKeys: ['Process Automation'],
    body: (
      <div className={styles.sectionBody}>
        <p>
          Only for Digital Chat Journeys. When switched on, the agent opens the
          Task Assistant with the ✦ button next to the Copilot bar (or by typing /)
          and runs tasks inside Copilot, like Cognigy workflows in the live product.
        </p>
        <ul>
          <li><strong>Task library</strong> — comes with the Book an Iceland Trip task ready to edit.</li>
          <li><strong>Book an Iceland Trip</strong> — the agent clicks Run (or View Form first); Copilot asks the customer in the chat, the answers fill the form, an off-script answer needs the agent (Resume), and the agent confirms the filled form.</li>
          <li><strong>Copilot conversation</strong> — each Question card has what Copilot asks and what the customer answers. Under &quot;Fills in the form&quot;, only the fields that question fills are shown, each with an input that matches the field (date, number or choice); click a + chip to add another field, or × to remove one. A question with no fields, like a confirmation, fills nothing. Tick &quot;Customer goes off-script&quot; to pause the task (shown as Needs agent) until the agent clicks Resume. Reorder or delete questions with the arrows and bin, or use Add question.</li>
          <li><strong>Recommendation</strong> — Manual (Task Assistant only), or Rule - Auto run, which starts the task on its own once the chat begins.</li>
          <li><strong>Completed</strong> — the agent summary plus the customer message, which the agent can edit, insert (✓) or send (➤).</li>
        </ul>
        <p className={styles.calloutTip}>
          <Tip /> Journeys that leave Process Automation off look exactly as before.
          Copilot Assist must be &quot;Yes&quot; for the Task Assistant to show to the agent.
        </p>
      </div>
    ),
  },
  {
    id: 'good-to-know',
    title: 'Good to Know',
    matchKeys: [],
    body: (
      <div className={styles.sectionBody}>
        <ul>
          <li>Your Journeys are saved to your account in the cloud — always there when you log back in, and teammates can reuse the ones you share.</li>
          <li>Only you can edit or delete a Journey you created. Anyone can play, star, or clone it, so no one can accidentally break your demo.</li>
          <li>The Copilot tab supports both quick, out-of-the-box demos and fully custom scripted conversations for a specific prospect's story.</li>
        </ul>
      </div>
    ),
  },
];

export default simulatorV2HelpSections;
